import axios from "axios"; // 引入axios
import { getApiBase, getLogBase, whenDomainReady } from "@/request/domain";

// 登录态 token
function readToken() {
    return JSON.parse(localStorage.getItem("AppStore"))?.UserInfo?.token || "";
}

// 创建axios请求
// 不写死 baseURL：域名由后台云控下发（config 接口的 domain.api），在请求拦截器里取当前生效值
const instance = axios.create({
    timeout: 15000,
    headers: {
        "Content-Type": "application/json;charset=UTF-8",
    },
});

//tips request 请求拦截器
instance.interceptors.request.use(
    async (config) => {
        // 启动早期域名可能还没拉到，这里排队等一下，避免打到兜底域名上
        await whenDomainReady();
        config.baseURL = getApiBase();

        const token = readToken();
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
    },
    (error) => {
        console.log("请求错误==>", error.message);
        return Promise.reject(error);
    }
);
//tips response 响应拦截器
instance.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        console.log("响应错误==>", error.message);
        return Promise.reject(error);
    }
);

// 配置接口（/workai/index/config）实例：域名固定，不参与云控。
// 云控的两个域名就是它下发的，所以它自己既不能等域名就绪（会死等自己），也不跟随下发值。
const configInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: 15000,
    headers: {
        "Content-Type": "application/json;charset=UTF-8",
    },
});

configInstance.interceptors.request.use(
    (config) => {
        const token = readToken();
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
    },
    (error) => {
        console.log("配置请求错误==>", error.message);
        return Promise.reject(error);
    }
);

configInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        console.log("配置响应错误==>", error.message);
        return Promise.reject(error);
    }
);

// 埋点日志走独立域名（与业务接口不同），因此单独一个实例。
// 不带 Authorization：埋点参数全在 url 里，后端不需要 token；
// 跨域带自定义头会触发 OPTIONS 预检，日志服务未必处理。
const logInstance = axios.create({
    timeout: 15000,
    headers: {
        "Content-Type": "application/json;charset=UTF-8",
    },
});

logInstance.interceptors.request.use(
    async (config) => {
        await whenDomainReady();
        config.baseURL = getLogBase();
        return config;
    },
    (error) => {
        console.log("埋点请求错误==>", error.message);
        return Promise.reject(error);
    }
);

logInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        console.log("埋点响应错误==>", error.message);
        return Promise.reject(error);
    }
);

/**
 * tips 固定域名 Get：给配置接口用，不跟随云控域名
 * @param {String} url [请求的url地址]
 * @param {Object} params [请求携带参数]
 * @param {Number} timeout [超时时间，启动阶段拉配置时会压短]
 */
export function GetFixed(url, params = {}, timeout?: number) {
    return new Promise((resolve, reject) => {
        configInstance
            .get(url, { params, ...(timeout ? { timeout } : {}) })
            .then((res) => {
                resolve(res.data);
            })
            .catch((err) => {
                reject(err);
            });
    });
}

/**
 * tips 埋点日志专用 Get，走云控下发的日志域名
 * @param {String} url [请求的url地址]
 * @param {Object} params [请求携带参数]
 */
export function GetLog(url, params = {}) {
    return new Promise((resolve, reject) => {
        logInstance
            .get(url, { params })
            .then((res) => {
                resolve(res.data);
            })
            .catch((err) => {
                reject(err);
            });
    });
}

/**
 * tips Get方法，对应get请求
 * @param {String} url [请求的url地址]
 * @param {Object} params [请求携带参数]
 */
export function Get(url, params = {}) {
    return new Promise((resolve, reject) => {
        instance
            .get(url, { params })
            .then((res) => {
                resolve(res.data);
            })
            .catch((err) => {
                reject(err);
            });
    });
}

/**
 * tips Post方法，对应post请求
 * @param {String} url [请求的url地址]
 * @param {Object} params [请求携带参数]
 */
export function Post(url, params = {}) {
    return new Promise((resolve, reject) => {
        instance
            .post(url, params)
            .then((res) => {
                resolve(res.data);
            })
            .catch((err) => {
                reject(err.response?.data || err);
            });
    });
}

/**
 * tips PostForm 方法，对应 application/x-www-form-urlencoded 格式
 * @param {String} url
 * @param {Object} params
 */
export function PostForm(url, params = {}) {
    // 用 URLSearchParams 转表单格式
    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
            formData.append(key, params[key]);
        }
    });
    return new Promise((resolve, reject) => {
        instance
            .post(url, formData, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            })
            .then((res) => {
                resolve(res.data);
            })
            .catch((err) => {
                reject(err.response?.data || err);
            });
    });
}